import { useState, useId } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Copy, Key, Plug, Sparkles, Terminal, Unplug } from 'lucide-react';
import { api, type ClaudeConnection } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import ConfirmDialog from '../ui/ConfirmDialog';
import Badge from '../ui/Badge';
import StateBlock from '../ui/StateBlock';
import { formatDate, formatDateTime } from '../../utils/format';
import { mcpUrl, claudeCodeOAuthCommand, claudeCodeKeyCommand } from '../../utils/claudeConnect';

/** A command / address to copy, with its copy button. */
function CopyLine({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the text can be selected by hand */
    }
  };
  return (
    <div className="flex items-start gap-2 rounded-lg border border-surface-200 bg-surface-50 px-3 py-2">
      <code dir="ltr" className="flex-1 min-w-0 break-all font-mono text-xs text-surface-900 text-start">{value}</code>
      <button type="button" onClick={() => void copy()} className="btn btn-secondary btn-sm shrink-0" aria-label={`${t('claudeConnect.copy')}: ${label}`}>
        {copied ? <CheckCircle size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
        {copied ? t('claudeConnect.copied') : t('claudeConnect.copy')}
      </button>
    </div>
  );
}

function connectionName(c: ClaudeConnection, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (c.kind === 'personal_key') return c.label ? t('claudeConnect.keyNamed', { name: c.label }) : t('claudeConnect.keyUnnamed');
  return c.client_name || t('claudeConnect.oauthClient');
}

function ConnectionRow({ c, showUser, onRevoke }: { c: ClaudeConnection; showUser?: boolean; onRevoke: (c: ClaudeConnection) => void }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-surface-900 truncate">
          {showUser && <span>{c.user_name || c.user_email} · </span>}
          <bdi>{connectionName(c, t)}</bdi>
          {c.kind === 'personal_key' && c.secret_prefix && (
            <code dir="ltr" className="ms-2 font-mono text-xs text-surface-600">{c.secret_prefix}…</code>
          )}
        </p>
        <p className="text-xs text-surface-600">
          <Badge tone={c.kind === 'oauth' ? 'brand' : 'neutral'} className="me-2">
            {c.kind === 'oauth' ? t('claudeConnect.kindOAuth') : t('claudeConnect.kindKey')}
          </Badge>
          {t('claudeConnect.connectedSince', { date: formatDate(c.created_at) })}
          {c.last_used_at && <span> · {t('claudeConnect.lastUsed', { date: formatDateTime(c.last_used_at) })}</span>}
        </p>
      </div>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onRevoke(c)}>
        <Unplug size={14} aria-hidden="true" />
        {t('claudeConnect.disconnect')}
      </button>
    </li>
  );
}

/**
 * FIX-CLAUDE-B (D-314 §3, §5) — "חיבור ל-Claude" in the employee's profile:
 * the status (connected since…, disconnect), how to connect Claude Code and
 * the regular Claude, the personal key (the backup, acting as her only), and
 * for the OTE manager: who is connected, with disconnect.
 */
export default function ClaudeConnectPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const uid = useId();
  const isManager = useAuthStore((s) => s.user?.role === 'admin');
  const url = mcpUrl();
  const [keyLabel, setKeyLabel] = useState('');
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<{ c: ClaudeConnection; asManager: boolean } | null>(null);

  const mine = useQuery({ queryKey: ['claude-connections'], queryFn: () => api.getClaudeConnections() });
  const all = useQuery({ queryKey: ['claude-connections-all'], queryFn: () => api.getAllClaudeConnections(), enabled: isManager });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['claude-connections'] });
    void queryClient.invalidateQueries({ queryKey: ['claude-connections-all'] });
  };

  const createKey = useMutation({
    mutationFn: () => api.createClaudePersonalKey(keyLabel.trim() || undefined),
    onSuccess: (res) => {
      if (res.data?.key) setFreshKey(res.data.key);
      setKeyLabel('');
      refresh();
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('claudeConnect.keyError'))),
  });

  const revoke = useMutation({
    mutationFn: (v: { c: ClaudeConnection; asManager: boolean }) =>
      (v.asManager ? api.adminRevokeClaudeConnection(v.c.id) : api.revokeClaudeConnection(v.c.id)),
    onSuccess: () => {
      toast('success', t('claudeConnect.disconnected'));
      setRevoking(null);
      refresh();
    },
    onError: (err) => toast('error', apiErrorMessage(err, t('claudeConnect.disconnectError'))),
  });

  const myRows = mine.data?.data ?? [];

  return (
    <div className="card divide-y divide-surface-200">
      {/* Status */}
      <section className="py-6 first:pt-0">
        <h2 className="flex items-center gap-2 text-base font-semibold text-surface-900">
          <Sparkles size={18} aria-hidden="true" className="text-accent-text" />
          {t('claudeConnect.title')}
        </h2>
        <p className="text-sm text-surface-600 mt-0.5">{t('claudeConnect.intro')}</p>
        <div className="mt-4">
          {mine.isLoading ? (
            <StateBlock kind="loading" rows={2} />
          ) : mine.isError ? (
            <StateBlock kind="error" title={t('claudeConnect.loadError')} error={mine.error} onRetry={() => void mine.refetch()} retrying={mine.isFetching} />
          ) : myRows.length === 0 ? (
            <p className="text-sm text-surface-700">{t('claudeConnect.notConnected')}</p>
          ) : (
            <ul className="divide-y divide-surface-200 border-y border-surface-200" aria-label={t('claudeConnect.myConnections')}>
              {myRows.map((c) => <ConnectionRow key={c.id} c={c} onRevoke={(x) => setRevoking({ c: x, asManager: false })} />)}
            </ul>
          )}
        </div>
      </section>

      {/* Claude Code */}
      <section className="py-6">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-surface-900">
          <Terminal size={16} aria-hidden="true" />
          {t('claudeConnect.codeTitle')}
        </h3>
        <ol className="mt-2 list-decimal space-y-2 ps-5 text-sm text-surface-700">
          <li>
            {t('claudeConnect.codeStep1')}
            <div className="mt-1.5"><CopyLine value={claudeCodeOAuthCommand(url)} label={t('claudeConnect.codeTitle')} /></div>
          </li>
          <li>{t('claudeConnect.codeStep2')}</li>
          <li>{t('claudeConnect.codeStep3')}</li>
        </ol>
      </section>

      {/* The regular Claude (organization) */}
      <section className="py-6">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-surface-900">
          <Plug size={16} aria-hidden="true" />
          {t('claudeConnect.webTitle')}
        </h3>
        <ol className="mt-2 list-decimal space-y-2 ps-5 text-sm text-surface-700">
          <li>
            {t('claudeConnect.webStep1')}
            <div className="mt-1.5"><CopyLine value={url} label={t('claudeConnect.address')} /></div>
          </li>
          <li>{t('claudeConnect.webStep2')}</li>
        </ol>
      </section>

      {/* The personal key (backup) */}
      <section className="py-6">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-surface-900">
          <Key size={16} aria-hidden="true" />
          {t('claudeConnect.keyTitle')}
        </h3>
        <p className="mt-1 text-sm text-surface-600">{t('claudeConnect.keyIntro')}</p>
        {freshKey ? (
          <div className="mt-3 space-y-3 rounded-lg border border-success-200 bg-success-50 p-4" role="status">
            <p className="flex items-center gap-2 text-sm font-medium text-success-800">
              <CheckCircle size={16} aria-hidden="true" />
              {t('claudeConnect.keyShownOnce')}
            </p>
            <CopyLine value={freshKey} label={t('claudeConnect.keyTitle')} />
            <p className="text-sm text-surface-700">{t('claudeConnect.keyCommand')}</p>
            <CopyLine value={claudeCodeKeyCommand(url, freshKey)} label={t('claudeConnect.keyCommand')} />
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFreshKey(null)}>{t('claudeConnect.keyDone')}</button>
          </div>
        ) : (
          <form
            className="mt-3 flex flex-wrap items-end gap-2"
            onSubmit={(e) => { e.preventDefault(); createKey.mutate(); }}
          >
            <div className="min-w-0 flex-1 sm:max-w-xs">
              <label htmlFor={`${uid}-label`} className="label">{t('claudeConnect.keyLabel')}</label>
              <input
                id={`${uid}-label`}
                className="input"
                value={keyLabel}
                maxLength={80}
                onChange={(e) => setKeyLabel(e.target.value)}
                placeholder={t('claudeConnect.keyLabelPlaceholder')}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={createKey.isPending} aria-busy={createKey.isPending || undefined}>
              <Key size={16} aria-hidden="true" />
              {t('claudeConnect.keyCreate')}
            </button>
          </form>
        )}
      </section>

      {/* The OTE manager: who is connected */}
      {isManager && (
        <section className="py-6 last:pb-0">
          <h3 className="text-sm font-semibold text-surface-900">{t('claudeConnect.adminTitle')}</h3>
          <p className="mt-1 text-sm text-surface-600">{t('claudeConnect.adminIntro')}</p>
          <div className="mt-3">
            {all.isLoading ? (
              <StateBlock kind="loading" rows={2} />
            ) : all.isError ? (
              <StateBlock kind="error" title={t('claudeConnect.loadError')} error={all.error} onRetry={() => void all.refetch()} retrying={all.isFetching} />
            ) : (all.data?.data ?? []).length === 0 ? (
              <p className="text-sm text-surface-700">{t('claudeConnect.adminNone')}</p>
            ) : (
              <ul className="divide-y divide-surface-200 border-y border-surface-200" aria-label={t('claudeConnect.adminTitle')}>
                {(all.data?.data ?? []).map((c) => (
                  <ConnectionRow key={c.id} c={c} showUser onRevoke={(x) => setRevoking({ c: x, asManager: true })} />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!revoking}
        title={t('claudeConnect.disconnectTitle')}
        description={revoking ? t('claudeConnect.disconnectText', { name: connectionName(revoking.c, t) }) : ''}
        confirmLabel={t('claudeConnect.disconnect')}
        danger
        loading={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking)}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
}
